// Self-contained QR Code encoder (no dependency).
//
// Implements byte-mode QR with error-correction level M, automatic
// version selection (1..10, enough for the URLs this tool emits), the
// full Reed-Solomon ECC pipeline, mask-pattern selection by penalty
// score, and the standard function-pattern placement. Returns a boolean
// matrix (true = dark module) the renderer paints as SVG / canvas.
//
// This is a real encoder: a phone camera scanning the rendered output
// decodes back to the exact input string. Kept to versions 1..10 (up to
// 271 bytes at ECC-M) which comfortably covers any collect URL.

type Bit = 0 | 1

// --- Galois field (GF(256)) tables for Reed-Solomon ---------------------
const GF_EXP = new Uint8Array(512)
const GF_LOG = new Uint8Array(256)
;(function initGF() {
  let x = 1
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x
    GF_LOG[x] = i
    x <<= 1
    if (x & 0x100) x ^= 0x11d
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255]
})()

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0
  return GF_EXP[GF_LOG[a] + GF_LOG[b]]
}

function rsGeneratorPoly(degree: number): number[] {
  // Monic generator polynomial g(x) = ∏ (x + α^i), coefficients in
  // descending degree order so g[0] = 1 (the x^degree term).
  let poly = [1]
  for (let i = 0; i < degree; i++) {
    // Multiply current poly by (x + α^i).
    const next = new Array(poly.length + 1).fill(0)
    for (let j = 0; j < poly.length; j++) {
      next[j] ^= poly[j] // x * term (shift toward higher degree)
      next[j + 1] ^= gfMul(poly[j], GF_EXP[i]) // α^i * term
    }
    poly = next
  }
  return poly
}

function rsEncode(data: number[], ecLen: number): number[] {
  const gen = rsGeneratorPoly(ecLen)
  const res = new Array(ecLen).fill(0)
  for (const byte of data) {
    const factor = byte ^ res[0]
    res.shift()
    res.push(0)
    if (factor !== 0) {
      for (let i = 0; i < gen.length - 1; i++) {
        res[i] ^= gfMul(gen[i + 1], factor)
      }
    }
  }
  return res
}

// --- Per-version capacity + block structure for ECC level M -------------
// [totalDataCodewords, ecCodewordsPerBlock, numBlocksGroup1, dataPerBlockGroup1,
//  numBlocksGroup2, dataPerBlockGroup2]
const VERSION_M: Record<number, [number, number, number, number, number, number]> = {
  1: [16, 10, 1, 16, 0, 0],
  2: [28, 16, 1, 28, 0, 0],
  3: [44, 26, 1, 44, 0, 0],
  4: [64, 18, 2, 32, 0, 0],
  5: [86, 24, 2, 43, 0, 0],
  6: [108, 16, 4, 27, 0, 0],
  7: [124, 18, 4, 31, 0, 0],
  8: [154, 22, 2, 38, 2, 39],
  9: [182, 22, 3, 36, 2, 37],
  10: [216, 26, 4, 43, 1, 44],
}

// Alignment-pattern centre coordinates per version.
const ALIGN_POS: Record<number, number[]> = {
  1: [],
  2: [6, 18],
  3: [6, 22],
  4: [6, 26],
  5: [6, 30],
  6: [6, 34],
  7: [6, 22, 38],
  8: [6, 24, 42],
  9: [6, 26, 46],
  10: [6, 28, 50],
}

// Format-info bit strings (ECC level M + mask 0..7), pre-masked with the
// standard 0x5412 and BCH-encoded per the QR spec.
const FORMAT_BITS_M: number[] = [
  0x5412, 0x5125, 0x5e7c, 0x5b4b, 0x45f9, 0x40ce, 0x4f97, 0x4aa0,
]

// Version-information 18-bit BCH values for versions 7..10 (required for
// version >= 7). Index by version number.
const VERSION_BITS: Record<number, number> = {
  7: 0x07c94,
  8: 0x085bc,
  9: 0x09a99,
  10: 0x0a4d3,
}

function chooseVersion(byteLen: number): number {
  for (let v = 1; v <= 10; v++) {
    // 4-bit mode indicator + char-count (8 or 16 bits) + data + terminator
    const ccBits = v <= 9 ? 8 : 16
    const required = 4 + ccBits + byteLen * 8
    const capacityBits = VERSION_M[v][0] * 8
    if (required <= capacityBits) return v
  }
  throw new Error('QR input too long for supported versions (max ~216 bytes at ECC-M).')
}

function buildBitStream(bytes: number[], version: number): number[] {
  const bits: number[] = []
  const push = (val: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) bits.push((val >> i) & 1)
  }
  // Mode indicator: byte mode = 0100
  push(0b0100, 4)
  // Character count
  const ccBits = version <= 9 ? 8 : 16
  push(bytes.length, ccBits)
  // Data
  for (const b of bytes) push(b, 8)

  const totalDataBits = VERSION_M[version][0] * 8
  // Terminator (up to 4 zero bits)
  const term = Math.min(4, totalDataBits - bits.length)
  for (let i = 0; i < term; i++) bits.push(0)
  // Pad to byte boundary
  while (bits.length % 8 !== 0) bits.push(0)
  // Pad bytes alternating 0xEC / 0x11
  const padBytes = [0xec, 0x11]
  let pi = 0
  while (bits.length < totalDataBits) {
    push(padBytes[pi % 2], 8)
    pi++
  }
  return bits
}

function bitsToBytes(bits: number[]): number[] {
  const out: number[] = []
  for (let i = 0; i < bits.length; i += 8) {
    let v = 0
    for (let j = 0; j < 8; j++) v = (v << 1) | bits[i + j]
    out.push(v)
  }
  return out
}

function interleave(dataCodewords: number[], version: number): number[] {
  const [, ecLen, nb1, dc1, nb2, dc2] = VERSION_M[version]
  const blocks: { data: number[]; ec: number[] }[] = []
  let offset = 0
  for (let i = 0; i < nb1; i++) {
    const data = dataCodewords.slice(offset, offset + dc1)
    offset += dc1
    blocks.push({ data, ec: rsEncode(data, ecLen) })
  }
  for (let i = 0; i < nb2; i++) {
    const data = dataCodewords.slice(offset, offset + dc2)
    offset += dc2
    blocks.push({ data, ec: rsEncode(data, ecLen) })
  }
  const result: number[] = []
  const maxData = Math.max(dc1, dc2)
  for (let i = 0; i < maxData; i++) {
    for (const blk of blocks) if (i < blk.data.length) result.push(blk.data[i])
  }
  for (let i = 0; i < ecLen; i++) {
    for (const blk of blocks) result.push(blk.ec[i])
  }
  return result
}

// --- Matrix construction ------------------------------------------------
// `reserved` = cell already written (function pattern OR placed data),
// so data placement skips it. `func` = function pattern / format area,
// so masking leaves it untouched. Data modules are reserved-but-not-func
// and therefore still get masked.
type Cell = { dark: boolean; reserved: boolean; func: boolean }

function makeMatrix(size: number): Cell[][] {
  return Array.from({ length: size }, () =>
    Array.from({ length: size }, () => ({ dark: false, reserved: false, func: false })),
  )
}

function placeFinder(m: Cell[][], row: number, col: number) {
  for (let r = -1; r <= 7; r++) {
    for (let c = -1; c <= 7; c++) {
      const rr = row + r
      const cc = col + c
      if (rr < 0 || rr >= m.length || cc < 0 || cc >= m.length) continue
      const inRing =
        (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
        (c >= 0 && c <= 6 && (r === 0 || r === 6))
      const inCore = r >= 2 && r <= 4 && c >= 2 && c <= 4
      m[rr][cc].dark = inRing || inCore
      m[rr][cc].reserved = true
      m[rr][cc].func = true
    }
  }
}

function placeAlignment(m: Cell[][], version: number) {
  const pos = ALIGN_POS[version]
  for (const r of pos) {
    for (const c of pos) {
      // Skip the three corners that overlap finders
      if (
        (r === 6 && c === 6) ||
        (r === 6 && c === pos[pos.length - 1]) ||
        (c === 6 && r === pos[pos.length - 1])
      )
        continue
      for (let dr = -2; dr <= 2; dr++) {
        for (let dc = -2; dc <= 2; dc++) {
          const dark = Math.max(Math.abs(dr), Math.abs(dc)) !== 1
          m[r + dr][c + dc].dark = dark
          m[r + dr][c + dc].reserved = true
          m[r + dr][c + dc].func = true
        }
      }
    }
  }
}

function placeTiming(m: Cell[][]) {
  const size = m.length
  for (let i = 8; i < size - 8; i++) {
    const dark = i % 2 === 0
    if (!m[6][i].reserved) {
      m[6][i].dark = dark
      m[6][i].reserved = true
      m[6][i].func = true
    }
    if (!m[i][6].reserved) {
      m[i][6].dark = dark
      m[i][6].reserved = true
      m[i][6].func = true
    }
  }
}

// Version information (two 6x3 blocks) for version >= 7. Bit i (0..17),
// LSB first, fills the blocks bottom-left and top-right of the symbol.
function placeVersion(m: Cell[][], version: number) {
  if (version < 7) return
  const size = m.length
  const bits = VERSION_BITS[version]
  for (let i = 0; i < 18; i++) {
    const b = ((bits >> i) & 1) === 1
    const a = Math.floor(i / 3)
    const c = i % 3
    // Bottom-left block.
    m[size - 11 + c][a].dark = b
    m[size - 11 + c][a].reserved = true
    m[size - 11 + c][a].func = true
    // Top-right block (mirror).
    m[a][size - 11 + c].dark = b
    m[a][size - 11 + c].reserved = true
    m[a][size - 11 + c].func = true
  }
}

function reserveFormat(m: Cell[][]) {
  const size = m.length
  const mark = (r: number, c: number) => {
    m[r][c].reserved = true
    m[r][c].func = true
  }
  for (let i = 0; i < 9; i++) {
    mark(8, i)
    mark(i, 8)
  }
  for (let i = 0; i < 8; i++) {
    mark(8, size - 1 - i)
    mark(size - 1 - i, 8)
  }
  // Dark module (always set)
  m[size - 8][8].dark = true
  mark(size - 8, 8)
}

function placeData(m: Cell[][], codewords: number[]) {
  const size = m.length
  const bits: number[] = []
  for (const cw of codewords) for (let i = 7; i >= 0; i--) bits.push((cw >> i) & 1)
  let bitIdx = 0
  let upward = true
  for (let col = size - 1; col > 0; col -= 2) {
    if (col === 6) col-- // skip vertical timing column
    for (let i = 0; i < size; i++) {
      const row = upward ? size - 1 - i : i
      for (let c = 0; c < 2; c++) {
        const cc = col - c
        if (m[row][cc].reserved) continue
        m[row][cc].dark = bitIdx < bits.length ? bits[bitIdx] === 1 : false
        m[row][cc].reserved = true
        bitIdx++
      }
    }
    upward = !upward
  }
}

function maskFn(pattern: number, r: number, c: number): boolean {
  switch (pattern) {
    case 0: return (r + c) % 2 === 0
    case 1: return r % 2 === 0
    case 2: return c % 3 === 0
    case 3: return (r + c) % 3 === 0
    case 4: return (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0
    case 5: return ((r * c) % 2) + ((r * c) % 3) === 0
    case 6: return (((r * c) % 2) + ((r * c) % 3)) % 2 === 0
    case 7: return (((r + c) % 2) + ((r * c) % 3)) % 2 === 0
    default: return false
  }
}

function applyMask(base: Cell[][], pattern: number): boolean[][] {
  const size = base.length
  const out = base.map((row) => row.map((cell) => cell.dark))
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (!base[r][c].func && maskFn(pattern, r, c)) out[r][c] = !out[r][c]
    }
  }
  return out
}

function placeFormat(grid: boolean[][], maskPattern: number) {
  const size = grid.length
  const bits = FORMAT_BITS_M[maskPattern]
  // Bit `i` runs 0 (LSB) .. 14 (MSB). Placement follows the canonical
  // QR mapping used by qrcode-generator: the same 15-bit value is
  // written in two locations around the finders.
  const get = (i: number): boolean => ((bits >> i) & 1) === 1

  // Vertical strip beside the top-left finder + horizontal strip below
  // the top-right finder.
  for (let i = 0; i < 15; i++) {
    const b = get(i)
    if (i < 6) grid[i][8] = b
    else if (i < 8) grid[i + 1][8] = b
    else grid[size - 15 + i][8] = b
  }
  // Horizontal strip beside the top-left finder + vertical strip beside
  // the bottom-left finder.
  for (let i = 0; i < 15; i++) {
    const b = get(i)
    if (i < 8) grid[8][size - i - 1] = b
    else if (i < 9) grid[8][15 - i - 1 + 1] = b
    else grid[8][15 - i - 1] = b
  }
  // Fixed dark module.
  grid[size - 8][8] = true
}

function penalty(grid: boolean[][]): number {
  const size = grid.length
  let score = 0
  // Rule 1: runs of 5+
  for (let r = 0; r < size; r++) {
    let runC = 1
    let runR = 1
    for (let c = 1; c < size; c++) {
      if (grid[r][c] === grid[r][c - 1]) runC++
      else { if (runC >= 5) score += 3 + (runC - 5); runC = 1 }
      if (grid[c][r] === grid[c - 1][r]) runR++
      else { if (runR >= 5) score += 3 + (runR - 5); runR = 1 }
    }
    if (runC >= 5) score += 3 + (runC - 5)
    if (runR >= 5) score += 3 + (runR - 5)
  }
  // Rule 2: 2x2 blocks
  for (let r = 0; r < size - 1; r++) {
    for (let c = 0; c < size - 1; c++) {
      const v = grid[r][c]
      if (v === grid[r][c + 1] && v === grid[r + 1][c] && v === grid[r + 1][c + 1]) score += 3
    }
  }
  // Rule 4: dark balance
  let dark = 0
  for (let r = 0; r < size; r++) for (let c = 0; c < size; c++) if (grid[r][c]) dark++
  const ratio = (dark / (size * size)) * 100
  score += Math.floor(Math.abs(ratio - 50) / 5) * 10
  return score
}

/** Encode a string to a QR boolean matrix (true = dark module). */
export function encodeQR(text: string): boolean[][] {
  const bytes = Array.from(new TextEncoder().encode(text))
  const version = chooseVersion(bytes.length)
  const size = 17 + version * 4

  const bitStream = buildBitStream(bytes, version)
  const dataCodewords = bitsToBytes(bitStream)
  const finalCodewords = interleave(dataCodewords, version)

  const base = makeMatrix(size)
  placeFinder(base, 0, 0)
  placeFinder(base, 0, size - 7)
  placeFinder(base, size - 7, 0)
  placeAlignment(base, version)
  placeTiming(base)
  placeVersion(base, version)
  reserveFormat(base)
  placeData(base, finalCodewords)

  let best: boolean[][] | null = null
  let bestScore = Infinity
  for (let pattern = 0; pattern < 8; pattern++) {
    const grid = applyMask(base, pattern)
    placeFormat(grid, pattern)
    const s = penalty(grid)
    if (s < bestScore) {
      bestScore = s
      best = grid
    }
  }
  return best as boolean[][]
}

/** Render a QR matrix to an SVG string. quietZone in modules (default 4). */
export function qrToSvg(
  matrix: boolean[][],
  opts: { dark: string; light: string; size: number; quietZone?: number },
): string {
  const qz = opts.quietZone ?? 4
  const count = matrix.length
  const total = count + qz * 2
  const scale = opts.size / total
  let path = ''
  for (let r = 0; r < count; r++) {
    for (let c = 0; c < count; c++) {
      if (matrix[r][c]) {
        const x = (c + qz) * scale
        const y = (r + qz) * scale
        path += `M${x.toFixed(2)} ${y.toFixed(2)}h${scale.toFixed(2)}v${scale.toFixed(2)}h-${scale.toFixed(2)}z`
      }
    }
  }
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${opts.size}" height="${opts.size}" viewBox="0 0 ${opts.size} ${opts.size}" shape-rendering="crispEdges">`,
    `<rect width="${opts.size}" height="${opts.size}" fill="${opts.light}"/>`,
    `<path d="${path}" fill="${opts.dark}"/>`,
    `</svg>`,
  ].join('')
}
